import { Box, Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'
import { formatCurrency } from '../../../utils/formatCurrency'
import { roundCurrency } from '../../../utils/roundCurrency'

interface ConfirmAnotherPaymentDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  totalCashToClose: number
  alreadySubmittedAmount: number
  additionalAmount: number
}

export function ConfirmAnotherPaymentDialog({
  open,
  onCancel,
  onConfirm,
  totalCashToClose,
  alreadySubmittedAmount,
  additionalAmount,
}: ConfirmAnotherPaymentDialogProps) {
  const newTotal = roundCurrency(alreadySubmittedAmount + additionalAmount)
  const remainingBalance = Math.max(0, roundCurrency(totalCashToClose - newTotal))
  const hasTotalCashToClose = totalCashToClose > 0

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onCancel}>
      <DialogTitle>Submit Additional Payment</DialogTitle>
      <DialogContent>
        {/* Total Cash to Close - only show if we have it */}
        {hasTotalCashToClose && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Total Cash to Close
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatCurrency(totalCashToClose)}
              </Typography>
            </Box>
            <Box sx={{ borderTop: '1px solid #e0e0e0', mb: 2 }} />
          </>
        )}

        {/* Calculation Breakdown */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Already Submitted
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatCurrency(alreadySubmittedAmount)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
            + Additional Payment
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formatCurrency(additionalAmount)}
          </Typography>
        </Box>

        {/* Calculation Divider */}
        <Box sx={{ borderTop: '2px solid #333', my: 2 }} />

        {/* New Total Result */}
        <Box sx={{ textAlign: 'center', mb: hasTotalCashToClose ? 3 : 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            = New Total After Additional Payment
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formatCurrency(newTotal)}
          </Typography>
        </Box>

        {/* Remaining Balance - only show if we have total */}
        {hasTotalCashToClose && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pr: 1 }}>
              Remaining balance after submitting additional payment
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              {formatCurrency(remainingBalance)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
        <Button onClick={onCancel} variant="text" color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Submit additional payment
        </Button>
      </Box>
    </Dialog>
  )
}
